// App.tsx — Oriora Speaking Coach. Local-first speaking-exam practice:
//   • IELTS mock speaking (Part 1 interview → Part 2 cue card with prep/talk timers →
//     Part 3 discussion) — examiner questions spoken by DEVICE TTS with an animated
//     examiner face (lazy-loaded Three.js primitives head; lip-sync from the vendored
//     MIT TalkingHead text→viseme rules, timed off TTS boundary events).
//   • TOEFL-style simulated interview (2026 "Take an Interview" format).
//   • Listen & Repeat — TTS speaks, you repeat, scored LOCALLY (no AI call, free).
//   • Practice history + band trend, all on-device (SQLite via Tauri IPC).
//
// HONESTY FENCE (visible in the UI, enforced server-side): AI feedback covers Fluency &
// Coherence, Lexical Resource, and Grammatical Range & Accuracy from the TRANSCRIPT ONLY;
// pronunciation needs audio analysis and is NOT scored; results are an ESTIMATED band
// range, never a guaranteed or official score.
//
// ⚠️ DO NOT re-add "your voice never leaves the device". It is FALSE, and it was shipped here
// for weeks. Dictation uses the Web Speech API, which on iOS is Apple's WebKit implementation:
// Safari sends the audio to APPLE'S CLOUD SERVICE to transcribe it (that is what the system
// "Access Speech Recognition" prompt is asking permission for). There is no on-device mode
// exposed to the web engine. What IS true, and what we actually promise: **ORIORA never
// receives the audio** (only the text transcript is sent, for one billed feedback call per
// part) and **Oriora doesn't retain it**. Privacy = we don't RETAIN it, never "it never leaves".

import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { invoke, listen, platform } from "./tauri-web";
import { saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { AddFundsMenu } from "./AddFundsMenu";
import {
  store, bandRange, relativeDays, todayLocal, parseFeedback,
  type FeedbackRow,
  EXAM_LABELS, MODE_LABELS, PART_LABELS, TOPIC_SUGGESTIONS,
  type Session, type Turn, type Exam, type Part, type SpeakingFeedback, type TrendPoint,
} from "./store";
import { getQuestions, getFeedback, openWebLogin, cloudBackupSave, cloudBackupLoad, BrainError, type BrainErrorCode, type CueCard } from "./brain";
import { speakExaminer, ttsAvailable, type SpeakHandle } from "./speech";
import AccentLab from "./AccentLab";
import { repeatScore, targetWordMatches, pickSentence, LEVEL_LABELS, type RepeatLevel } from "./listen-repeat";
import { OrioraBadge } from "./OrioraBadge";
import CallOverlay from "./live/CallOverlay";
import { buildExport } from "./export";
import { BackupMenu } from "./BackupMenu";
import Chat from "./chat/Chat";
import {
  Sparkles, Trash2, X, Lock, WifiOff, RefreshCw, AlertCircle, Copy, Check,
  Coins, Clock, History, Mic, Play, Square, Volume2, Repeat, TrendingUp,
  GraduationCap, MessageSquare,
} from "lucide-react";

// The avatar (three.js) loads ONLY when rendered — the app fully works with it off.
const ExaminerAvatar = lazy(() => import("./avatar/ExaminerAvatar"));

const APP_SCHEME = "ielts-speaking-coach";
const APP_NAME = "Oriora Speaking Coach";
const AVATAR_KEY = "oriora.speaking.avatar.v1";
const LEVEL_KEY = "oriora.speaking.level.v1";
// Ask the candidate's level once (remembered) so the examiner calibrates question difficulty —
// review: "app didn't ask me about my level… didn't feel like a professional practice."
const SPEAKING_LEVELS = [
  { id: "building", label: "Building", sub: "≈ band 4–5", hint: "a lower-intermediate candidate — keep questions clear and everyday, no obscure vocabulary" },
  { id: "improving", label: "Improving", sub: "≈ band 5.5–6.5", hint: "an intermediate candidate aiming for band 6.5 — natural exam-level questions with some follow-ups" },
  { id: "advanced", label: "Advanced", sub: "band 7+", hint: "an advanced candidate aiming for band 7+ — challenging, abstract questions and probing follow-ups" },
] as const;
function getStoredLevel(): string { try { return localStorage.getItem(LEVEL_KEY) || "improving"; } catch { return "improving"; } }
function saveLevel(id: string) { try { localStorage.setItem(LEVEL_KEY, id); } catch { /* ignore */ } }
function levelHint(id: string): string { return SPEAKING_LEVELS.find((l) => l.id === id)?.hint ?? ""; }
const PRONUNCIATION_NOTE =
  "Pronunciation needs audio analysis — it isn't scored in v1. This is an estimated band range from your transcript, not an official score.";

// Sim/screenshot rig: 1 = bypass consent+login so shot runs can reach every screen.
// ⚠️ MUST be 0 in any shipped build (per-app ledger checks this).
const SCREENSHOT = 0;

// ── "THE ROOM" (2026-08-06 redesign) — brass on ink-green; the speaking line ──
import glyphIeltsMock from "./assets/glyph-ielts-mock.svg";
import glyphInterview from "./assets/glyph-interview.svg";
import glyphListenRepeat from "./assets/glyph-listen-repeat.svg";
import glyphDebrief from "./assets/glyph-debrief.svg";
import momentEmptyRoom from "./assets/moment-empty-room.svg";

// The ambient motif: one hairline that bends only where a voice is.
function SpeakLine({ live, flat }: { live?: boolean; flat?: boolean }) {
  return (
    <span className={`speak-line${live ? " live" : ""}${flat ? " flat" : ""}`} aria-hidden="true">
      <svg viewBox="0 0 320 26" preserveAspectRatio="none">
        {live
          ? <path d="M0 13 H90 C110 13 118 4 132 4 S154 22 168 22 190 6 204 6 218 13 232 13 H320" />
          : <path d="M0 13 H320" />}
      </svg>
    </span>
  );
}

/**
 * Sound carrying from a mouth — two arcs radiating. Replaces the generic lucide `MessageSquare`:
 * this app is spoken practice, not messaging, and sibling Interview Prep already owns the speech
 * bubble. The control's outline moved too — Speaking Coach, Study, Closet and Plant Diary all
 * rendered the same 54px/999px/--accent circle, byte for byte (Guideline 4.3(a)).
 */
function VoiceMark({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M5.4 9.2v5.6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      <path d="M9.2 6.4v11.2" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      <path d="M14.4 8.6a4.8 4.8 0 0 1 0 6.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      <path d="M17.8 5.6a9.1 9.1 0 0 1 0 12.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2 #62, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground.
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns ielts-speaking-coach://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // ielts-speaking-coach://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  if (SCREENSHOT) return <Home email="you@oriora.app" onSignedOut={() => {}} />;
  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented) {
    return <AiConsent brand={APP_NAME}
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted) return <LoadingGate />;
  if (!authed) return <Login />;
  return <Home email={email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function LoadingGate() {
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "1.5rem" }}>
      <div style={{ textAlign: "center" }}>
        <div className="empty-art" style={{ marginBottom: "0.9rem" }}><GraduationCap size={34} /></div>
        <div className="muted row" style={{ justifyContent: "center", gap: "0.4rem", fontSize: "var(--fs-sm)" }}>
          <Sparkles size={13} className="pulse-soft" style={{ color: "var(--accent)" }} /> Opening the practice room…
        </div>
      </div>
    </main>
  );
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="fade-up" style={{ width: "100%", maxWidth: 380, padding: "2.25rem 0.5rem", textAlign: "center" }}>
        <h1 className="serif" style={{ fontSize: "1.95rem", margin: "0 0 0.4rem" }}>Speaking Coach</h1>
        <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.6, marginBottom: "0.9rem" }}>
          Talk with a coach in any language you're learning — or sit a mock exam. Out loud, either way.
        </p>
        <SpeakLine flat />
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.6, margin: "1rem 0 1.4rem", textAlign: "left" }}>
          Your practice — transcripts, saved lessons, benchmark recordings and your band trend — is
          stored on this device, and leaves it only when you send it somewhere: a backup goes through
          Oriora to your own GitHub or Google Drive, and an AI action sends what that action needs. A
          live call streams your speech to the AI provider that runs the conversation; the mock exam
          and drills use your device's own dictation. Oriora retains none of it afterwards.
        </p>
        <button className="btn btn-primary btn-pill" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos(APP_SCHEME);
              else if (p === "macos") await signInMac(APP_SCHEME);
              // Web: same-origin login — the Supabase session is shared on the orioralabs.com origin.
              else if (p === "web") window.location.href = `/login?next=/${APP_SCHEME}/`;
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? (<><Sparkles size={14} className="pulse-soft" /> Signing in…</>) : "Sign in with Oriora"}
        </button>
        <div className="muted row" style={{ justifyContent: "center", gap: "0.3rem", fontSize: "var(--fs-xxs)", marginTop: "1rem" }}>
          <Lock size={11} /> Pay per use from your Oriora balance — no subscription
        </div>
      </div>
    </main>
  );
}

// ── Home — hero, mode cards, history, chat ─────────────────────────────────────

type View =
  | { kind: "home" }
  | { kind: "practice"; session: Session }
  | { kind: "repeat"; session: Session };

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [trend, setTrend] = useState<TrendPoint[]>([]);
  const [view, setView] = useState<View>({ kind: "home" });
  const [starting, setStarting] = useState<Exam | null>(null); // which start modal is open
  const [accentLab, setAccentLab] = useState(false);
  const [liveCall, setLiveCall] = useState(false);             // the general coach call (home's lead door)
  const [confirmDelete, setConfirmDelete] = useState<Session | null>(null);
  const [detailFor, setDetailFor] = useState<Session | null>(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  const [isDesktop, setIsDesktop] = useState(true);
  useEffect(() => { try { setIsDesktop(platform() !== "ios" && platform() !== "android"); } catch { /* keep default */ } }, []);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const exportRef = useRef<(() => void) | null>(null);

  const [allFeedbacks, setAllFeedbacks] = useState<FeedbackRow[]>([]);

  const reload = useCallback(async () => {
    setSessions(await store.listSessions({ limit: 50000 }));
    setTrend(await store.bandTrend());
    try { setAllFeedbacks(await store.listAllFeedbacks()); } catch { setAllFeedbacks([]); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions (Actions menu in the macOS menu bar): New IELTS mock / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "mock": setStarting("ielts"); break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const answeredTotal = useMemo(() => sessions.reduce((n, s) => n + s.answered_count, 0), [sessions]);
  const latestBand = useMemo(() => (trend.length ? trend[trend.length - 1].band_mid : null), [trend]);

  // ── THE CRITERIA (ledger row 2b). IELTS is marked per criterion, and this app already scores all
  // three separately AND stores a grounded note for each — then the home showed ONE aggregate band.
  // An overall number tells a candidate nothing they can act on; "which of the three is holding me
  // down, and is it moving" is the only question that changes what they practise next. The data was
  // there the whole time, buried inside feedback_json.
  //
  // 🔒 The app's standing honesty rule holds: these are ESTIMATES from a transcript, pronunciation
  // is not scored, and nothing here is presented as an official band or a prediction.
  const criteria = useMemo(() => {
    const KEYS = [
      { key: "fluency_coherence", label: "Fluency & coherence" },
      { key: "lexical_resource", label: "Lexical resource" },
      { key: "grammar", label: "Grammar" },
    ] as const;
    const parsed = allFeedbacks
      .map((f) => ({ at: f.created_at, fb: parseFeedback(f.feedback_json) }))
      .filter((x): x is { at: string; fb: SpeakingFeedback } => x.fb !== null);
    if (!parsed.length) return [];
    return KEYS.map(({ key, label }) => {
      const series = parsed
        .map((x) => ({ at: x.at, band: x.fb[key].band, note: x.fb[key].note }))
        .filter((x) => x.band > 0);
      if (!series.length) return { key, label, band: null, delta: null, note: "", count: 0 };
      const latest = series[series.length - 1];
      // Direction = latest vs the mean of everything BEFORE it. Comparing against a single earlier
      // session would swing on one noisy transcript.
      const before = series.slice(0, -1);
      const delta = before.length
        ? latest.band - before.reduce((a, x) => a + x.band, 0) / before.length
        : null;
      return { key, label, band: latest.band, delta, note: latest.note, count: series.length };
    });
  }, [allFeedbacks]);

  // The weakest scored criterion — what a candidate would sensibly work on next. Descriptive only:
  // it names the lowest estimate, it does not instruct.
  const weakest = useMemo(() => {
    const scored = criteria.filter((c) => c.band != null);
    if (scored.length < 2) return null;
    return scored.reduce((a, b) => ((b.band as number) < (a.band as number) ? b : a));
  }, [criteria]);

  const doExport = useCallback(() => {
    if (sessions.length === 0) return;
    const { csv, filename } = buildExport(sessions);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [sessions]);
  exportRef.current = doExport;

  // Body is the scroll container: full-screen view swaps must land at the top.
  useEffect(() => { try { document.body.scrollTo(0, 0); window.scrollTo(0, 0); } catch { /* ignore */ } }, [view.kind, starting]);

  async function startPractice(exam: Exam, topic: string, focusPart: "" | "p1" | "p2" | "p3" = "") {
    const mode = exam === "ielts" ? "mock" : "interview";
    const session = await store.createSession({ exam, mode, topic, focus_part: focusPart });
    setStarting(null);
    await reload();
    setView({ kind: "practice", session });
  }

  async function startRepeat() {
    const session = await store.createSession({ exam: "toefl", mode: "listen_repeat" });
    await reload();
    setView({ kind: "repeat", session });
  }

  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteSession(id);
    await reload();
  }

  if (starting) {
    return (
      <main className="page">
        <StartModal exam={starting} onClose={() => setStarting(null)}
          onStart={(topic, focusPart) => void startPractice(starting, topic, focusPart)} />
      </main>
    );
  }
  if (view.kind === "practice") {
    return <PracticeView session={view.session} offline={offline}
      onExit={() => { setView({ kind: "home" }); void reload(); }} />;
  }
  if (view.kind === "repeat") {
    return <ListenRepeatView session={view.session}
      onExit={() => { setView({ kind: "home" }); void reload(); }} />;
  }
  if (accentLab) {
    return <AccentLab onExit={() => { setAccentLab(false); void reload(); }} />;
  }

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      padding: "max(1.5rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      <header className="rail">
        <span className="rail-title">Speaking Coach</span>
        <div className="rail-actions row" style={{ gap: "0.35rem", flexWrap: "nowrap" }}>
          <button className="icon-btn" aria-label="Ask about your practice" title="Ask about your practice"
            style={{ color: "var(--accent)", border: "1px solid var(--line-strong)", borderRadius: 999, width: 36, height: 36 }}
            onClick={() => setChatOpen(true)}>
            <MessageSquare size={17} />
          </button>
          <BackupMenu
            appName="Speaking Coach" fileSlug="speaking-coach" appScheme={APP_SCHEME}
            hasData={sessions.length > 0} onRestored={reload}
            onExportCsv={isDesktop && sessions.length > 0 ? doExport : undefined}
          />
          <AccountMenu email={email} appSlug={APP_SCHEME} appName={APP_NAME} onSignedOut={onSignedOut} />
          <AddFundsMenu />
        </div>
      </header>

      <div className="room-open fade-up">
        <h1 className="serif">The room is ready.</h1>
        <p className="muted">Talk with a coach in any language — or step into an exam room.</p>
        {/* READY-STATE R1 (2026-09-25, corrected) — the price shape, ABOVE the doors.
            The matrix row "free app, per-call wallet — no subscription, no trial to trap" appeared
            ZERO times on the signed-in screen (measured: "subscription"/"per use"/"wallet"/
            "balance" all 0). It lives at :248 on the SIGN-IN screen, which a returning customer
            never sees again, while every door below costs money.
            It was first placed UNDER the doors and that was wrong: measured y=837 on a 390x844
            phone, so only ~7px of the line was inside the fold, the text cut and its right end
            under the chat launcher. A price disclosure 7px inside the viewport is absent. Here it
            sits under the subtitle, fully visible, clear of the launcher, and appears exactly once. */}
        <p className="muted room-cost">
          <Lock size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
          Pay per use from your Oriora balance — no subscription
        </p>
      </div>

      {offline && (
        <div role="status" className="notice notice-warn fade-up" style={{ marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Offline — the examiner can't fetch new questions. Listen & Repeat and everything already here still work.</span>
        </div>
      )}

      {/* R1 — the doors lead */}
      <div className="doors fade-up">
        {/* The LIVE CALL leads — general practice in any language is the app's
            main job now; the exam rooms are doors beneath it (the general-first
            pivot, DIRECTION-GENERAL-SPEAKING.md). */}
        <button className="door door--primary" onClick={() => setLiveCall(true)}>
          <span className="door-body">
            <span className="door-glyph" style={{ fontSize: 30, lineHeight: 1, width: 34, textAlign: "center" }}>📞</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="door-title serif">Talk with your coach</span>
              <span className="door-desc">A real conversation in any language — it listens, replies out loud, and you can interrupt any time.</span>
            </span>
            <span className="door-chevron">›</span>
          </span>
        </button>
        <button className="door door--secondary" onClick={() => setStarting("ielts")}>
          <span className="door-body">
            <img className="door-glyph" src={glyphIeltsMock} alt="" />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="door-title serif">IELTS mock speaking</span>
              <span className="door-desc">Part 1 interview → Part 2 cue card → Part 3 discussion. Real timers: 1 min to prepare, 2 min to talk.</span>
            </span>
            <span className="door-chevron">›</span>
          </span>
        </button>
        <button className="door door--secondary" onClick={() => setStarting("toefl")}>
          <span className="door-body">
            <img className="door-glyph" src={glyphInterview} alt="" />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="door-title serif">Take an Interview</span>
              <span className="door-desc">TOEFL 2026 format · spoken questions, answered out loud.</span>
            </span>
            <span className="door-chevron">›</span>
          </span>
        </button>
        <button className="door door--secondary" onClick={() => void startRepeat()}>
          <span className="door-body">
            <img className="door-glyph" src={glyphListenRepeat} alt="" />
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="door-title serif">Listen &amp; Repeat <span className="badge badge-make" style={{ marginLeft: 6, verticalAlign: "2px" }}>free · on-device</span></span>
              <span className="door-desc">Hear a sentence, say it back. Scored on this phone.</span>
            </span>
            <span className="door-chevron">›</span>
          </span>
        </button>
        <button className="door door--secondary" onClick={() => setAccentLab(true)}>
          <span className="door-body">
            <span className="door-glyph" style={{ fontSize: 26, lineHeight: 1, width: 34, textAlign: "center" }}>🎯</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span className="door-title serif">Accent Lab</span>
              <span className="door-desc">Shadow one voice, melody first — accent drills, Mandarin tones, goals with a countdown, and the monthly benchmark that proves the progress.</span>
            </span>
            <span className="door-chevron">›</span>
          </span>
        </button>
      </div>


      {/* R3 — the standing follows the doors */}
      {sessions.length === 0 ? (
        <div className="empty-room fade-up" style={{ marginTop: "1.4rem" }}>
          <div className="empty-art"><img src={momentEmptyRoom} alt="" style={{ width: 88 }} /></div>
          <h3 className="serif">Nobody has spoken here yet</h3>
          <p className="muted">Walk in through any door above. Your transcripts, bands and trend collect down here afterwards — on this phone.</p>
          <p className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "0.9rem" }}>
            <Lock size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
            Oriora keeps no recordings — your practice stays on this phone unless you back it up.
          </p>
          <button className="chat-entry" style={{ marginTop: "1.1rem" }} onClick={() => setChatOpen(true)}>
            <MessageSquare size={15} /> <span style={{ flex: 1, textAlign: "left" }}>Ask about your practice</span>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>searches this phone only</span> ›
          </button>
        </div>
      ) : (
        <>
          <div className="sec-head" style={{ marginTop: "1.5rem" }}><h2>When you step out</h2></div>
          <CriterionBoard criteria={criteria} weakest={weakest} />

          {trend.length > 1 && (
            <div className="trend" aria-label="Band trend">
              {trend.slice(-8).map((t, i, arr) => {
                const h = Math.max(8, Math.min(44, ((t.band_mid - 3.5) / 5.5) * 44));
                return <span key={t.session_id} className={`trend-bar${i === 0 ? " first" : ""}${i === arr.length - 1 ? " latest" : ""}`} style={{ height: h }} />;
              })}
              {latestBand != null && <span className="trend-val tnum serif">~{latestBand.toFixed(1)}</span>}
            </div>
          )}
          {trend.length > 1 && <div className="trend-note muted">Band trend · each bar is one scored session · midpoint of the estimated range</div>}

          <div className="sec-head" style={{ marginTop: "1.1rem" }}>
            <h2>Your practice {sessions.length > 0 && <span className="muted tnum" style={{ fontWeight: 400, letterSpacing: 0, textTransform: "none" }}>· {sessions.length}</span>}</h2>
          </div>
          <div>
            {sessions.map((s) => (
              <SessionTile key={s.id} s={s}
                onOpen={() => setDetailFor(s)}
                onDelete={() => setConfirmDelete(s)} />
            ))}
          </div>

          <button className="chat-entry" onClick={() => setChatOpen(true)}>
            <MessageSquare size={15} /> <span style={{ flex: 1, textAlign: "left" }}>Ask about your practice</span>
            <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>searches this phone only</span> ›
          </button>
        </>
      )}

      {detailFor && <SessionDetailModal session={detailFor} onClose={() => setDetailFor(null)} />}

      {confirmDelete && (
        <ModalShell title="Delete session?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", margin: "0 0 1.25rem", lineHeight: 1.55 }}>
            Delete this <strong style={{ color: "var(--ink-1)" }}>{MODE_LABELS[confirmDelete.mode]}</strong>
            {confirmDelete.topic ? <> on "<strong style={{ color: "var(--ink-1)" }}>{confirmDelete.topic}</strong>"</> : null} with its transcripts and feedback? This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
            <button className="btn btn-pill" onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-danger btn-pill" onClick={() => doDelete(confirmDelete.id)}>Delete</button>
          </div>
        </ModalShell>
      )}

      {!chatOpen && (
        <button className="chat-bubble" aria-label="Open chat — ask about your practice" onClick={() => setChatOpen(true)}>
          <VoiceMark size={22} />
        </button>
      )}
      <aside className={`rbc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="rbc-head">
          <span className="serif" style={{ fontSize: "1.1rem" }}>Ask about your practice</span>
          <button className="link" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
      {/* The home's lead door opens the same call sheet the chat uses — one call
          surface, two ways in. Its transcript stays in the call (the chat keeps
          its own copy when opened from there). */}
      {liveCall && <CallOverlay onClose={() => setLiveCall(false)} />}
    </main>
  );
}

/** THE THREE CRITERIA — the app's own primitive (ledger row 2b).
 *
 *  IELTS speaking is marked per criterion, so the object a candidate actually works on is a
 *  CRITERION, not a session. This app already scored all three and wrote a grounded observation for
 *  each, then showed a single aggregate band on the home screen — a number that cannot tell anyone
 *  what to do differently. The board puts the three side by side with where each sits, which way it
 *  has moved, and the last thing the examiner-model actually observed about it.
 *
 *  🔒 Estimates, not scores. Pronunciation needs audio and is not marked. The arrow describes the
 *  candidate's own recorded history — it is never a prediction of an official band. */
function CriterionBoard({ criteria, weakest }: {
  criteria: { key: string; label: string; band: number | null; delta: number | null; note: string; count: number }[];
  weakest: { key: string; label: string; band: number | null } | null;
}) {
  if (!criteria.length || criteria.every((c) => c.band == null)) return null;
  return (
    <section className="crit-board fade-up" aria-label="Your three assessed criteria">
      <div className="crit-head">
        <span className="crit-title">Where you are, by criterion</span>
        <span className="crit-sub muted">estimated from your transcripts</span>
      </div>
      {criteria.map((c) => {
        const dir = c.delta == null ? null : c.delta > 0.24 ? "up" : c.delta < -0.24 ? "down" : "flat";
        return (
          <div key={c.key} className={`crit-row${weakest && weakest.key === c.key ? " lowest" : ""}`}>
            <div className="crit-name">{c.label}</div>
            <div className="crit-band tnum">
              {c.band != null ? c.band.toFixed(1) : "—"}
              {dir && (
                <span className={`crit-dir ${dir}`} aria-label={dir === "up" ? "above your earlier average" : dir === "down" ? "below your earlier average" : "in line with your earlier average"}>
                  {dir === "up" ? "▲" : dir === "down" ? "▼" : "•"}
                </span>
              )}
            </div>
            {c.note && <div className="crit-note muted">{c.note}</div>}
          </div>
        );
      })}
      {weakest?.band != null && (
        <div className="crit-foot muted">
          Lowest estimate right now: <strong>{weakest.label}</strong> — pronunciation isn't scored here.
        </div>
      )}
    </section>
  );
}

function PracticeHero({ sessions, answered, latestBand, trend }: { sessions: number; answered: number; latestBand: number | null; trend: TrendPoint[] }) {
  return (
    <div className="fade-up" style={{ marginBottom: "1.1rem" }}>
      <div className="hero" style={{ marginBottom: sessions === 0 ? 0 : "0.75rem" }}>
        <div className="eyebrow" style={{ color: "var(--accent-soft-ink)", opacity: 0.85, marginBottom: "0.35rem" }}>
          <Mic size={13} /> Your speaking practice
        </div>
        <div className="serif tnum" style={{ fontSize: "2.1rem", fontWeight: 600, lineHeight: 1.05 }}>
          {latestBand != null ? `~${latestBand.toFixed(1)} band` : sessions === 0 ? "Ready when you are" : `${sessions} ${sessions === 1 ? "session" : "sessions"}`}
        </div>
        <div style={{ fontSize: "var(--fs-sm)", opacity: 0.85, marginTop: 2 }}>
          {latestBand != null
            ? "latest estimated band midpoint — from transcripts, pronunciation not scored"
            : sessions === 0 ? "Run a mock to get your first estimated band range" : `${answered} answers recorded`}
        </div>
      </div>
      {trend.length > 1 && (
        <div className="stat-grid">
          <div className="stat" style={{ gridColumn: "1 / -1" }}>
            <div className="stat-lbl"><TrendingUp size={12} /> Band trend (estimated)</div>
            <BandSpark trend={trend} />
            <div className="stat-sub">each bar = one scored session · midpoint of the estimated range</div>
          </div>
        </div>
      )}
    </div>
  );
}

// Tiny dependency-free spark bars for the band trend (bands 4..9 mapped to bar heights).
function BandSpark({ trend }: { trend: TrendPoint[] }) {
  const pts = trend.slice(-16);
  return (
    <div className="row" style={{ gap: 4, alignItems: "flex-end", height: 44, marginTop: 6 }} aria-label="Band trend chart">
      {pts.map((p) => {
        const h = Math.max(6, Math.min(44, ((p.band_mid - 3.5) / 5.5) * 44));
        return (
          <span key={p.session_id} title={`${p.date.slice(0, 10)} — ~${p.band_mid.toFixed(1)}`}
            style={{ width: 14, height: h, borderRadius: 4, background: "var(--accent)", opacity: 0.35 + 0.65 * (h / 44), flexShrink: 0 }} />
        );
      })}
      <span className="tnum" style={{ fontSize: "var(--fs-xs)", color: "var(--ink-2)", marginLeft: 6 }}>
        ~{pts[pts.length - 1].band_mid.toFixed(1)}
      </span>
    </div>
  );
}

function ModeCard({ icon, title, desc, cta, onClick, free }: { icon: React.ReactNode; title: string; desc: string; cta: string; onClick: () => void; free?: boolean }) {
  return (
    <div className="tile" style={{ alignItems: "flex-start" }}>
      <span className="thumb" style={{ flexShrink: 0 }}>{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: "0.4rem" }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", color: "var(--ink-1)" }}>{title}</div>
          {free && <span className="badge badge-make" style={{ flexShrink: 0 }}>free · on-device</span>}
        </div>
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 3, lineHeight: 1.5 }}>{desc}</div>
      </div>
      <button className="btn btn-primary btn-sm btn-pill" style={{ flexShrink: 0, alignSelf: "center" }} onClick={onClick}>{cta}</button>
    </div>
  );
}

function SessionTile({ s, onOpen, onDelete }: { s: Session; onOpen: () => void; onDelete: () => void }) {
  const band = bandRange(s.band_low, s.band_high);
  return (
    <div className="tile">
      <button style={{ flex: 1, minWidth: 0, cursor: "pointer", background: "none", border: "none", textAlign: "left", padding: 0 }} onClick={onOpen}>
        <div className="row" style={{ gap: "0.4rem", flexWrap: "nowrap" }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", color: "var(--ink-1)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textTransform: "capitalize" }}>
            {s.topic || MODE_LABELS[s.mode]}
          </div>
          <span className="badge" style={{ flexShrink: 0 }}>{EXAM_LABELS[s.exam]}</span>
          {band !== "—" && <span className="badge badge-make" style={{ flexShrink: 0 }}>est. {band}</span>}
          {s.mode === "listen_repeat" && s.avg_score != null && <span className="badge badge-make" style={{ flexShrink: 0 }}>{Math.round(s.avg_score)}%</span>}
        </div>
        <div className="row" style={{ gap: "0.5rem", marginTop: 4 }}>
          <span style={{ fontSize: 10, color: "var(--ink-3)" }}>{MODE_LABELS[s.mode]}</span>
          <span className="row tnum" style={{ gap: 3, fontSize: 10, color: "var(--ink-3)" }}>
            <Clock size={10} /> {relativeDays(s.created_at)} · {s.answered_count}/{s.turn_count} answered
          </span>
        </div>
      </button>
      <div className="row" style={{ gap: 2, flexShrink: 0 }}>
        <IconBtn title="View transcripts & feedback" onClick={onOpen}><History size={15} /></IconBtn>
        <IconBtn title="Delete session" danger onClick={onDelete}><Trash2 size={15} /></IconBtn>
      </div>
    </div>
  );
}

function IconBtn({ title, onClick, children, danger }: { title: string; onClick: () => void; children: React.ReactNode; danger?: boolean }) {
  return (
    <button className={`icon-btn${danger ? " danger" : ""}`} title={title} aria-label={title}
      onClick={(ev) => { ev.stopPropagation(); onClick(); }}>
      {children}
    </button>
  );
}

type FocusPart = "" | "p1" | "p2" | "p3";
const FOCUS_CHOICES: { id: FocusPart; label: string; sub: string }[] = [
  { id: "",   label: "Full mock",  sub: "all three parts" },
  { id: "p1", label: "Part 1",     sub: "interview questions" },
  { id: "p2", label: "Part 2",     sub: "cue card, 2-min talk" },
  { id: "p3", label: "Part 3",     sub: "discussion" },
];

function StartModal({ exam, onClose, onStart }: { exam: Exam; onClose: () => void; onStart: (topic: string, focusPart: FocusPart) => void }) {
  const [topic, setTopic] = useState("");
  const [level, setLevel] = useState<string>(getStoredLevel());
  const [focus, setFocus] = useState<FocusPart>("");
  return (
    <div className="doorway" role="dialog" aria-modal="true">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.4rem" }}>
        <button className="icon-btn" aria-label="Close" onClick={onClose}><X size={17} /></button>
        <span className="eyebrow">Before you go in</span>
        <span style={{ width: 36 }} />
      </div>
      <div className="doorway-head">
        <h1 className="serif">{exam === "ielts" ? "IELTS mock speaking" : "Take an Interview"}</h1>
        <p className="muted">
          {exam === "ielts"
            ? "Three parts, spoken. The examiner asks; you answer out loud or type. Questions cost one Oriora call per part, feedback one more."
            : "A simulated spoken interview in the 2026 TOEFL format. Questions cost one Oriora call, feedback one more."}
        </p>
      </div>
      {/* L2 table-stake: every competitor lets a student drill ONE part. Revising Part 2 should
          not mean sitting the whole 14-minute exam. */}
      {exam === "ielts" && (
        <div className="doorway-field">
          <label className="field-label">What you'll sit</label>
          <div className="opt-grid">
            {FOCUS_CHOICES.map((f) => (
              <button key={f.id || "full"} type="button" className={`opt${focus === f.id ? " selected" : ""}`} onClick={() => setFocus(f.id)}>
                <span className="opt-name">{f.label}</span>
                <span className="opt-sub">{f.sub}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="doorway-field">
        <label className="field-label">Topic — or leave it to the examiner</label>
        <input list="topics" value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. travel" style={{ marginBottom: "0.55rem" }} />
        <datalist id="topics">{TOPIC_SUGGESTIONS.map((t) => <option key={t} value={t} />)}</datalist>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
          {TOPIC_SUGGESTIONS.slice(0, 6).map((t) => (
            <button key={t} type="button" className={`chip toggle${topic === t ? " selected" : ""}`} onClick={() => setTopic(topic === t ? "" : t)}>{t}</button>
          ))}
        </div>
      </div>

      <div className="doorway-field">
        <label className="field-label">So the examiner pitches it right</label>
        <div className="opt-grid three">
          {SPEAKING_LEVELS.map((l) => (
            <button key={l.id} type="button" className={`opt${level === l.id ? " selected" : ""}`} style={{ textAlign: "center" }} onClick={() => setLevel(l.id)}>
              <span className="opt-name">{l.label}</span>
              <span className="opt-sub">{l.sub}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="doorway-dock">
        <button className="btn btn-primary btn-pill" style={{ width: "100%", minHeight: 54 }}
          onClick={() => { saveLevel(level); onStart(topic.trim(), exam === "ielts" ? focus : ""); }}>
          <Play size={15} /> Enter the room
        </button>
        <div className="doorway-note muted">You'll hear the first question straight away.</div>
      </div>
    </div>
  );
}

// ── The examiner panel: avatar + spoken question ───────────────────────────────

function useAvatarPref(): [boolean, (v: boolean) => void] {
  const [on, setOn] = useState<boolean>(() => {
    try { return localStorage.getItem(AVATAR_KEY) !== "0"; } catch { return true; }
  });
  const set = (v: boolean) => {
    setOn(v);
    try { localStorage.setItem(AVATAR_KEY, v ? "1" : "0"); } catch { /* ignore */ }
  };
  return [on, set];
}

function ExaminerPanel({ question, autoSpeak }: { question: string; autoSpeak: boolean }) {
  const [avatarOn, setAvatarOn] = useAvatarPref();
  const [speaking, setSpeaking] = useState(false);
  const visemeRef = useRef<string>("sil");
  const handleRef = useRef<SpeakHandle | null>(null);

  const speak = useCallback((text: string) => {
    handleRef.current?.cancel();
    handleRef.current = speakExaminer(text, {
      onViseme: (v) => { visemeRef.current = v; },
      onStart: () => setSpeaking(true),
      onEnd: () => { setSpeaking(false); visemeRef.current = "sil"; },
    });
  }, []);

  // Speak each new question once, automatically.
  useEffect(() => {
    if (autoSpeak && question) speak(question);
    return () => { handleRef.current?.cancel(); };
  }, [question, autoSpeak, speak]);

  return (
    <div className={`stage${speaking ? " speaking" : ""}${avatarOn ? "" : " compact"}`}>
      {avatarOn && (
        <div className="stage-face">
          <Suspense fallback={<div className="skeleton-block" style={{ width: "100%", height: "100%", borderRadius: 999 }} />}>
            <ExaminerAvatar visemeRef={visemeRef} speaking={speaking} height={176} />
          </Suspense>
        </div>
      )}
      <div className={`stage-label${speaking ? " live" : ""}`}>
        <span className="live-dot" /> {speaking ? "Examiner speaking" : "Examiner"}
      </div>
      <SpeakLine live={speaking} flat={!speaking} />
      <p className="q-text serif" style={{ whiteSpace: "pre-wrap" }}>{question}</p>
      <div className="q-tools row" style={{ gap: "0.4rem" }}>
        <button className="chip" onClick={() => (speaking ? handleRef.current?.cancel() : speak(question))}>
          {speaking ? (<><Square size={11} /> Stop</>) : (<><Volume2 size={12} /> {ttsAvailable() ? "Say it again" : "Voice unavailable here"}</>)}
        </button>
        <button className="chip toggle" onClick={() => setAvatarOn(!avatarOn)}>{avatarOn ? "Face off" : "Face on"}</button>
      </div>
    </div>
  );
}

// ── Dictation (continuous) — device SpeechRecognition into an editable transcript ─
function getSR(): (new () => any) | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
}

function DictationBox({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef<any>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* ignore */ } }, []);
  const SR = getSR();

  const toggle = () => {
    if (listening) { try { recRef.current?.stop(); } catch { /* ignore */ } setListening(false); return; }
    if (!SR) return;
    try {
      const rec = new SR();
      rec.lang = "en-US";
      rec.continuous = true;
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.onresult = (e: any) => {
        let added = "";
        for (let i = e.resultIndex; i < e.results.length; i++) {
          if (e.results[i].isFinal) added += ` ${e.results[i][0]?.transcript ?? ""}`;
        }
        if (added.trim()) onChange(`${valueRef.current} ${added.trim()}`.trim());
      };
      rec.onend = () => setListening(false);
      rec.onerror = () => setListening(false);
      recRef.current = rec;
      setListening(true);
      rec.start();
    } catch { setListening(false); }
  };

  return (
    <div className="answer">
      <div className={`answer-label${listening ? " live" : ""}`}>
        {listening ? (<><span className="live-dot" /> You're speaking</>) : "Your answer"}
        {listening && <span className="muted" style={{ marginLeft: "auto", fontSize: "var(--fs-xxs)", letterSpacing: 0, textTransform: "none" }}>tap a word to fix it</span>}
      </div>
      <textarea className="dictation-text" rows={5} value={value}
        placeholder={placeholder ?? "Tap the microphone and answer out loud. Your words appear here, and you can fix anything the dictation mishears."}
        onChange={(e) => onChange(e.target.value)} style={{ resize: "vertical" }} />
      <div className="answer-note muted">Dictation is your device's own speech recognition — only the text you keep is sent, and only when you ask for feedback.</div>
      <div className="dock">
        <div className="dock-rule" />
        {listening && (
          <div className="level-meter" aria-hidden="true">
            {[14, 22, 10, 18, 24, 12].map((h, i) => <span key={i} className="lvl-bar" style={{ height: h }} />)}
          </div>
        )}
        <div className="dock-row row" style={{ gap: "0.5rem", flexWrap: "nowrap" }}>
          {SR ? (
            <button type="button" className={`mic-btn${listening ? " live" : ""}`} onClick={toggle}>
              {listening ? (<><Square size={14} /> Stop</>) : (<><Mic size={15} /> Answer out loud</>)}
            </button>
          ) : (
            <span className="muted" style={{ fontSize: "var(--fs-xxs)" }}>use the keyboard mic to dictate</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── PracticeView — the running mock / interview ────────────────────────────────

type MockPart = Exclude<Part, "repeat">;
type Phase =
  | { kind: "loading" }
  | { kind: "error"; err: AskErr }
  | { kind: "ask"; turns: Turn[]; idx: number }
  | { kind: "prep"; cue: CueCard; turn: Turn; secondsLeft: number }
  | { kind: "talk"; cue: CueCard; turn: Turn; secondsLeft: number }
  | { kind: "part-done"; turns: Turn[] }
  | { kind: "feedback"; fb: SpeakingFeedback; part: MockPart };

type AskErr = { code: BrainErrorCode | "empty"; message: string };

const MOCK_PARTS: MockPart[] = ["p1", "p2", "p3"];

function PracticeView({ session, offline, onExit }: { session: Session; offline: boolean; onExit: () => void }) {
  // A focused session practises ONE part; a full mock walks all three. (L2: every competitor
  // lets a student drill Part 2 without sitting the whole 14-minute exam.)
  const parts: MockPart[] =
    session.mode === "mock"
      ? (["p1", "p2", "p3"].includes(session.focus_part) ? [session.focus_part as MockPart] : MOCK_PARTS)
      : ["interview"];
  const [partIdx, setPartIdx] = useState(0);
  const part = parts[partIdx];
  const [phase, setPhase] = useState<Phase>({ kind: "loading" });
  const [draft, setDraft] = useState("");
  const [busyFeedback, setBusyFeedback] = useState(false);
  // Coaching on THIS answer, before you commit to it. Bogdan: "let them go back and forth."
  // Ephemeral on purpose — a draft you rewrote must not drag your band average around; only the
  // part's saved answers feed the stored feedback and the trend.
  const [answerFb, setAnswerFb] = useState<SpeakingFeedback | null>(null);
  const [coaching, setCoaching] = useState(false);
  const [coachErr, setCoachErr] = useState<string | null>(null);
  const [topic, setTopic] = useState(session.topic);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopTimer = () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
  useEffect(() => () => stopTimer(), []);

  const loadPart = useCallback(async (p: MockPart) => {
    setPhase({ kind: "loading" });
    setDraft("");
    try {
      // Already fetched (e.g. re-entering after an error)? Reuse the stored turns.
      const existing = (await store.listTurns(session.id)).filter((t) => t.part === p);
      if (existing.length > 0) {
        const firstOpen = existing.findIndex((t) => !t.answered_at);
        if (p === "p2") {
          const t = existing[0];
          const cue: CueCard = { topic: t.prompt, bullets: t.hint ? t.hint.split("\n").filter(Boolean) : [] };
          if (t.answered_at) setPhase({ kind: "part-done", turns: existing });
          else setPhase({ kind: "prep", cue, turn: t, secondsLeft: 60 });
        } else if (firstOpen === -1) {
          setPhase({ kind: "part-done", turns: existing });
        } else {
          setPhase({ kind: "ask", turns: existing, idx: firstOpen });
        }
        return;
      }
      // ONE billed call per "start part" action. Fold the candidate's stored level into the
      // prompt so the examiner calibrates difficulty; the display/stored topic stays clean.
      const lvl = levelHint(getStoredLevel());
      const qTopic = lvl ? `${topic ? `${topic}. ` : ""}Pitch the questions for ${lvl}.` : topic;
      const qs = await getQuestions(session.exam, p, qTopic);
      if (p === "p2" && qs.cue_card) {
        const cue = qs.cue_card;
        const turns = await store.addTurns(session.id, "p2", [{ prompt: cue.topic, hint: cue.bullets.join("\n") }]);
        if (!topic) { setTopic(cue.topic); await store.setTopic(session.id, cue.topic); }
        setPhase({ kind: "prep", cue, turn: turns[0], secondsLeft: 60 });
      } else {
        const turns = await store.addTurns(session.id, p, qs.questions.map((q) => ({ prompt: q })));
        if (turns.length === 0) { setPhase({ kind: "error", err: { code: "empty", message: "No questions came back — try again." } }); return; }
        setPhase({ kind: "ask", turns, idx: 0 });
      }
    } catch (e) {
      if (e instanceof BrainError) setPhase({ kind: "error", err: { code: e.code, message: e.message } });
      else setPhase({ kind: "error", err: { code: "unavailable", message: "The request failed — please try again." } });
    }
  }, [session.id, session.exam, topic]);

  useEffect(() => { void loadPart(part); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [partIdx]);

  // Part-2 timers.
  function startPrepCountdown(cue: CueCard, turn: Turn) {
    stopTimer();
    timerRef.current = setInterval(() => {
      setPhase((ph) => {
        if (ph.kind !== "prep") return ph;
        if (ph.secondsLeft <= 1) { stopTimer(); return startTalk(cue, turn); }
        return { ...ph, secondsLeft: ph.secondsLeft - 1 };
      });
    }, 1000);
  }
  function startTalk(cue: CueCard, turn: Turn): Phase {
    stopTimer();
    timerRef.current = setInterval(() => {
      setPhase((ph) => {
        if (ph.kind !== "talk") return ph;
        if (ph.secondsLeft <= 1) { stopTimer(); return { ...ph, secondsLeft: 0 }; }
        return { ...ph, secondsLeft: ph.secondsLeft - 1 };
      });
    }, 1000);
    return { kind: "talk", cue, turn, secondsLeft: 120 };
  }
  useEffect(() => {
    if (phase.kind === "prep" && !timerRef.current) startPrepCountdown(phase.cue, phase.turn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase.kind]);

  // One billed call, on the answer you just gave. The customer decides whether it's worth it;
  // the button says so plainly.
  async function coachThisAnswer(question: string) {
    const text = draft.trim();
    if (text.length < 40) { setCoachErr("Say (or type) a bit more first — coaching needs a few sentences."); return; }
    setCoaching(true); setCoachErr(null);
    try {
      const fb = await getFeedback(part as Exclude<Part, "repeat">, text, { exam: session.exam, topic, questions: [question] });
      setAnswerFb(fb);
    } catch (e) {
      setCoachErr(e instanceof BrainError ? e.message : "Couldn't fetch coaching — try again.");
    } finally { setCoaching(false); }
  }

  async function saveAnswerAndNext() {
    if (phase.kind !== "ask") return;
    const t = phase.turns[phase.idx];
    await store.recordTranscript(t.id, draft);
    const updated = phase.turns.map((x, i) => (i === phase.idx ? { ...x, transcript: draft.trim(), answered_at: new Date().toISOString() } : x));
    setDraft("");
    setAnswerFb(null); setCoachErr(null);
    if (phase.idx + 1 < phase.turns.length) setPhase({ kind: "ask", turns: updated, idx: phase.idx + 1 });
    else setPhase({ kind: "part-done", turns: updated });
  }

  async function saveTalk() {
    if (phase.kind !== "talk") return;
    stopTimer();
    setAnswerFb(null); setCoachErr(null);
    await store.recordTranscript(phase.turn.id, draft);
    const t = { ...phase.turn, transcript: draft.trim(), answered_at: new Date().toISOString() };
    setDraft("");
    setPhase({ kind: "part-done", turns: [t] });
  }

  async function runFeedback(turns: Turn[]) {
    setBusyFeedback(true);
    try {
      const answered = turns.filter((t) => t.transcript.trim());
      const transcript = answered.map((t) => t.transcript.trim()).join("\n\n");
      const questions = turns.map((t) => t.prompt);
      // ONE billed call per "Get feedback" action.
      const fb = await getFeedback(part, transcript, { exam: session.exam, topic, questions });
      await store.addFeedback(session.id, part, fb);
      setPhase({ kind: "feedback", fb, part });
    } catch (e) {
      if (e instanceof BrainError) setPhase({ kind: "error", err: { code: e.code, message: e.message } });
      else setPhase({ kind: "error", err: { code: "unavailable", message: "Feedback failed — please try again." } });
    } finally { setBusyFeedback(false); }
  }

  const isLastPart = partIdx === parts.length - 1;

  return (
    <main className="page room">
      <div className="room-top">
        <button className="btn-ghost" style={{ background: "none", border: 0, color: "var(--ink-2)", cursor: "pointer", font: "inherit", padding: "0.3rem 0", display: "inline-flex", alignItems: "center", gap: 5 }} onClick={onExit}>
          <X size={14} /> End
        </button>
        <span className="room-where muted tnum">
          {session.mode === "mock" ? `Part ${partIdx + 1} of ${parts.length} · ${PART_LABELS[part].split(" — ")[1] ?? PART_LABELS[part]}` : PART_LABELS[part]}
          {phase.kind === "ask" ? ` · ${phase.idx + 1}/${phase.turns.length}` : ""}
        </span>
      </div>
      {session.mode === "mock" && (
        <div className="part-ticks" aria-hidden="true">
          {parts.map((p, i) => (
            <span key={p} className={`tick${i < partIdx ? " done" : i === partIdx ? " now" : ""}`} />
          ))}
        </div>
      )}

      {offline && (
        <div role="status" className="notice notice-warn" style={{ marginBottom: "0.85rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>You're offline — questions and feedback can't be fetched right now.</span>
        </div>
      )}

      {phase.kind === "loading" && (
        <div aria-live="polite" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 9 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} className="pulse-soft" /> The examiner is preparing {PART_LABELS[part].toLowerCase()}…
          </span>
          <div className="skeleton-block" style={{ height: 170 }} />
          <div className="skeleton-line" style={{ width: "88%" }} />
          <div className="skeleton-line" style={{ width: "64%" }} />
        </div>
      )}

      {phase.kind === "error" && <AskError err={phase.err} onRetry={() => void loadPart(part)} />}

      {phase.kind === "ask" && (
        <div className="fade-up">
          <div className="muted tnum" style={{ fontSize: "var(--fs-xs)", marginBottom: 6 }}>
            Question {phase.idx + 1} of {phase.turns.length}
          </div>
          <ExaminerPanel question={phase.turns[phase.idx].prompt} autoSpeak />
          <DictationBox value={draft} onChange={setDraft} />

          {/* Answer → coaching → edit → answer again. The real coaching loop: a student learns by
              revising the answer they just gave, not by reading a verdict at the end of the exam. */}
          {answerFb && (
            <div style={{ marginTop: "0.85rem" }}>
              <FeedbackCard fb={answerFb} part={part} />
              <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.6rem" }}>
                <button className="btn btn-pill" onClick={() => { setAnswerFb(null); setCoachErr(null); }}>
                  Edit and try again
                </button>
              </div>
            </div>
          )}
          {coachErr && <div className="notice notice-warn" style={{ marginTop: "0.6rem" }}><span>{coachErr}</span></div>}

          <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem", marginTop: "0.85rem", flexWrap: "wrap" }}>
            <button className="btn btn-sm btn-pill" disabled={coaching || offline || draft.trim().length < 40}
              onClick={() => void coachThisAnswer(phase.turns[phase.idx].prompt)}
              title="One Oriora call — coaching on this answer before you commit to it">
              <Sparkles size={13} /> {coaching ? "Coaching…" : answerFb ? "Coach it again" : "Coach this answer"}
            </button>
            <button className="btn btn-primary btn-pill" disabled={!draft.trim()} onClick={() => void saveAnswerAndNext()}>
              {phase.idx + 1 < phase.turns.length ? "Save & next question" : "Save & finish part"}
            </button>
          </div>
          <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 4, textAlign: "right" }}>
            Coaching this answer is one Oriora call. The part's own feedback still comes at the end.
          </div>
        </div>
      )}

      {phase.kind === "prep" && (
        <div className="fade-up">
          <CueCardView cue={phase.cue} />
          <div className={`timer${phase.secondsLeft <= 10 ? " urgent" : ""}`}>
            <div className="timer-lbl">Preparing</div>
            <div className="timer-val serif tnum">{fmtSecs(phase.secondsLeft)}</div>
            <div className="timer-rule"><span className="timer-fill" style={{ width: `${(phase.secondsLeft / 60) * 100}%` }} /></div>
            <div className="timer-note muted">Make notes on paper if you like.{"\n"}Talking begins on its own.</div>
            <button className="btn btn-pill btn-sm" style={{ marginTop: "0.6rem" }} onClick={() => { stopTimer(); setPhase(startTalk(phase.cue, phase.turn)); }}>
              <Play size={13} /> Start talking now
            </button>
          </div>
        </div>
      )}

      {phase.kind === "talk" && (
        <div className="fade-up">
          <CueCardView cue={phase.cue} compact />
          <div className={`timer inline${phase.secondsLeft <= 15 ? " urgent" : ""}`} style={{ margin: "0.75rem 0 0.4rem" }}>
            <span className="timer-lbl">Talking</span>
            <span className="timer-val serif tnum" style={{ fontSize: 44 }}>{phase.secondsLeft > 0 ? fmtSecs(phase.secondsLeft) : "0:00"}</span>
            {phase.secondsLeft === 0 && <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>Time. Finish your sentence.</span>}
          </div>
          <div className="timer-rule" style={{ marginBottom: "0.6rem" }}><span className="timer-fill" style={{ width: `${((120 - phase.secondsLeft) / 120) * 100}%` }} /></div>
          <DictationBox value={draft} onChange={setDraft} placeholder="Give your talk — keep going for the full two minutes. Your words appear here." />

          {answerFb && (
            <div style={{ marginTop: "0.85rem" }}>
              <FeedbackCard fb={answerFb} part={part} />
              <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem", marginTop: "0.6rem" }}>
                <button className="btn btn-pill" onClick={() => { setAnswerFb(null); setCoachErr(null); }}>Edit and try again</button>
              </div>
            </div>
          )}
          {coachErr && <div className="notice notice-warn" style={{ marginTop: "0.6rem" }}><span>{coachErr}</span></div>}

          <div className="row" style={{ justifyContent: "space-between", gap: "0.5rem", marginTop: "0.85rem", flexWrap: "wrap" }}>
            <button className="btn btn-sm btn-pill" disabled={coaching || offline || draft.trim().length < 40}
              onClick={() => void coachThisAnswer(phase.cue.topic)}
              title="One Oriora call — coaching on this talk before you commit to it">
              <Sparkles size={13} /> {coaching ? "Coaching…" : answerFb ? "Coach it again" : "Coach this talk"}
            </button>
            <button className="btn btn-primary btn-pill" disabled={!draft.trim()} onClick={() => void saveTalk()}>Finish talk</button>
          </div>
          <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 4, textAlign: "right" }}>
            Coaching this talk is one Oriora call. The part's own feedback still comes at the end.
          </div>
        </div>
      )}

      {phase.kind === "part-done" && (
        <div className="fade-up recap" style={{ padding: "1.1rem 1.25rem" }}>
          <div className="eyebrow"><Check size={14} /> {PART_LABELS[part]} answered</div>
          <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.55, margin: "0.5rem 0 0.75rem" }}>
            Get band-style feedback on this part's transcript (one Oriora call), or move straight on.
          </p>
          <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            <button className="btn btn-primary btn-pill" disabled={busyFeedback || offline} onClick={() => void runFeedback(phase.turns)}>
              {busyFeedback ? (<><Sparkles size={13} className="pulse-soft" /> Assessing transcript…</>) : (<><Sparkles size={14} /> Get feedback</>)}
            </button>
            {!isLastPart && (
              <button className="btn btn-pill" disabled={busyFeedback} onClick={() => setPartIdx(partIdx + 1)}>Continue to {PART_LABELS[parts[partIdx + 1]].split(" — ")[0]} →</button>
            )}
            {isLastPart && <button className="btn btn-pill" disabled={busyFeedback} onClick={onExit}>Finish session</button>}
          </div>
          <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "0.6rem" }}>{PRONUNCIATION_NOTE}</div>
        </div>
      )}

      {phase.kind === "feedback" && (
        <div className="fade-up">
          <FeedbackCard fb={phase.fb} part={phase.part} />
          {isLastPart && <SessionCompleteNote session={session} fb={phase.fb} />}
          <div className="row" style={{ gap: "0.5rem", marginTop: "0.85rem", flexWrap: "wrap" }}>
            {!isLastPart && (
              <button className="btn btn-primary btn-pill" onClick={() => setPartIdx(partIdx + 1)}>Continue to {PART_LABELS[parts[partIdx + 1]].split(" — ")[0]} →</button>
            )}
            <button className="btn btn-pill" onClick={onExit}>{isLastPart ? "Finish — see my trend" : "End session here"}</button>
          </div>
        </div>
      )}
    </main>
  );
}

function fmtSecs(s: number): string {
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, "0")}`;
}

function CueCardView({ cue, compact }: { cue: CueCard; compact?: boolean }) {
  return (
    <div className="card" style={{ padding: compact ? "0.8rem 1rem" : "1.1rem 1.25rem", borderRadius: "var(--radius-lg)", borderLeft: "3px solid var(--accent)" }}>
      <div className="eyebrow" style={{ marginBottom: 4 }}>Cue card</div>
      <div style={{ fontWeight: 600, fontSize: compact ? "var(--fs-sm)" : "var(--fs-md)", color: "var(--ink-1)", lineHeight: 1.5 }}>{cue.topic}</div>
      {cue.bullets.length > 0 && !compact && (
        <>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0.5rem 0 0.2rem" }}>You should say:</div>
          <ul style={{ margin: 0, paddingLeft: "1.1rem" }}>
            {cue.bullets.map((b, i) => <li key={i} style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6 }}>{b}</li>)}
          </ul>
        </>
      )}
    </div>
  );
}

// End-of-session wrap — turns the last part's "Finish" from a dead-end into a coach hand-off:
// names the weakest criterion (concrete next step) + points to the chat coach for a plan.
// Review: "session went down, no feedback, no 'we did great here, improve here'… what's the point."
// The END-OF-SESSION verdict. Bogdan: "END-of-session feedback + band estimate — never
// 'no feedback, what's the point'." The old note showed the LAST PART's feedback and told the
// student their band was "saved in your history below". A student who has just finished a
// 14-minute mock exam wants the number, now.
//
// Costs NOTHING: every part's feedback is already stored, so the whole-session band, the
// weakest criterion ACROSS the session, and the next step are all computed on-device.
function SessionCompleteNote({ session, fb }: { session: Session; fb: SpeakingFeedback }) {
  const [rows, setRows] = useState<FeedbackRow[] | null>(null);
  useEffect(() => {
    let live = true;
    store.listFeedbacks(session.id).then((r) => { if (live) setRows(r); }).catch(() => setRows([]));
    return () => { live = false; };
  }, [session.id]);

  // Average across the parts that were actually scored (Listen & Repeat has no AI band).
  const scored = (rows ?? []).filter((r) => r.band_high > 0);
  const lo = scored.length ? scored.reduce((n, r) => n + r.band_low, 0) / scored.length : fb.band_low;
  const hi = scored.length ? scored.reduce((n, r) => n + r.band_high, 0) / scored.length : fb.band_high;

  // The weakest criterion across the WHOLE session, not just the last part.
  const totals: Record<string, { sum: number; n: number }> = {};
  for (const r of scored) {
    const f = parseFeedback(r.feedback_json);
    if (!f) continue;
    for (const [label, c] of [
      ["fluency & coherence", f.fluency_coherence],
      ["vocabulary (lexical resource)", f.lexical_resource],
      ["grammar (range & accuracy)", f.grammar],
    ] as const) {
      if (c.band > 0) { totals[label] ??= { sum: 0, n: 0 }; totals[label].sum += c.band; totals[label].n += 1; }
    }
  }
  const weakest = Object.entries(totals).map(([label, t]) => ({ label, band: t.sum / t.n }))
    .sort((a, b) => a.band - b.band)[0];

  return (
    <div className="recap fade-up" style={{ marginTop: "0.85rem", padding: "1.1rem 1.25rem" }} aria-live="polite">
      <div className="row" style={{ justifyContent: "space-between", gap: "0.6rem", alignItems: "baseline" }}>
        <span className="eyebrow"><Sparkles size={14} /> {["p1","p2","p3"].includes(session.focus_part) ? `${PART_LABELS[session.focus_part as Part].split(" — ")[0]} practice` : "Your whole mock"} — estimated band</span>
        <span className="serif tnum" style={{ fontSize: "1.6rem", fontWeight: 700, color: "var(--accent)" }}>
          {bandRange(lo, hi)}
        </span>
      </div>

      {scored.length > 1 && (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 4, margin: "0.6rem 0 0.2rem" }}>
          {scored.map((r) => (
            <div key={r.id} className="row" style={{ justifyContent: "space-between", fontSize: "var(--fs-xs)" }}>
              <span className="muted">{PART_LABELS[r.part as Part]}</span>
              <span className="tnum">{bandRange(r.band_low, r.band_high)}</span>
            </div>
          ))}
        </div>
      )}

      <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.55, marginTop: "0.6rem" }}>
        {weakest
          ? <>Across this session your lowest area was <strong>{weakest.label}</strong> — that's the one to target next.</>
          : <>Your band and trend are saved in your history.</>}
        {" "}This is an <strong>estimated</strong> range from your transcripts, not an official score — pronunciation isn't assessed here because it needs audio.
        {" "}Tap <strong>Chat</strong> and ask what to practise next; it can hand you Part 2 or Part 3 questions on the spot.
      </p>
    </div>
  );
}

// The feedback result — three criteria + the estimated band range + the honesty note.
function FeedbackCard({ fb, part }: { fb: SpeakingFeedback; part: Part }) {
  const crits = [
    { label: "Fluency & Coherence", c: fb.fluency_coherence },
    { label: "Lexical Resource", c: fb.lexical_resource },
    { label: "Grammatical Range & Accuracy", c: fb.grammar },
  ];
  return (
    <div aria-live="polite">
      <div className="band-hero">
        <div className="band-lbl">{PART_LABELS[part]} — estimated band range</div>
        <div className="band-val serif tnum revealing">{bandRange(fb.band_low, fb.band_high)}</div>
      </div>
      {/* THE HONESTY FENCE — first-class, above the fold, never small-print. */}
      <div className="fence">
        <div className="fence-row"><span>An estimated <strong>range</strong>, never an official score or a prediction of one.</span></div>
        <div className="fence-row"><span>Read from <strong>your transcript only</strong> — the words, not the sound of them.</span></div>
        <div className="fence-row"><span><strong>Pronunciation needs audio analysis — not scored in v1.</strong></span></div>
      </div>
      <div style={{ marginTop: "0.75rem" }}>
        {crits.map(({ label, c }) => (
          <div key={label} className="crit-read">
            <div className="crit-read-head">
              <span className="crit-read-name">{label}</span>
              {c.band > 0 && <span className="crit-read-band serif tnum">~{c.band.toFixed(1)}</span>}
            </div>
            {c.note && <div className="crit-read-note muted">{c.note}</div>}
          </div>
        ))}
      </div>
      {fb.strengths.length > 0 && (
        <div style={{ marginTop: "0.75rem" }}>
          <div className="stat-lbl">What worked</div>
          {fb.strengths.map((s, i) => (
            <div key={i} className="fact"><span className="fact-dot good" /><span className="fact-text">{s}</span></div>
          ))}
        </div>
      )}
      {fb.improvements.length > 0 && (
        <div style={{ marginTop: "0.5rem" }}>
          <div className="stat-lbl">To work on</div>
          {fb.improvements.map((s, i) => (
            <div key={i} className="fact"><span className="fact-dot watch" /><span className="fact-text">{s}</span></div>
          ))}
        </div>
      )}
      <div style={{ marginTop: 10 }}><OrioraBadge /></div>
      <div className="row" style={{ marginTop: 10, justifyContent: "flex-end" }}>
        <CopyButton text={copyableFeedback(fb, part)} />
      </div>
    </div>
  );
}

function copyableFeedback(fb: SpeakingFeedback, part: Part): string {
  const lines = [
    `${PART_LABELS[part]} — estimated band range ${bandRange(fb.band_low, fb.band_high)} (from transcript; pronunciation not scored)`,
    `Fluency & Coherence ~${fb.fluency_coherence.band.toFixed(1)}: ${fb.fluency_coherence.note}`,
    `Lexical Resource ~${fb.lexical_resource.band.toFixed(1)}: ${fb.lexical_resource.note}`,
    `Grammatical Range & Accuracy ~${fb.grammar.band.toFixed(1)}: ${fb.grammar.note}`,
  ];
  if (fb.strengths.length) lines.push("What worked:\n" + fb.strengths.map((s) => `• ${s}`).join("\n"));
  if (fb.improvements.length) lines.push("To work on:\n" + fb.improvements.map((s) => `• ${s}`).join("\n"));
  return lines.join("\n\n");
}

// ── Listen & Repeat — entirely on-device, free ─────────────────────────────────

type RepeatPhase = "idle" | "played" | "scored";

function ListenRepeatView({ session, onExit }: { session: Session; onExit: () => void }) {
  const [level, setLevel] = useState<RepeatLevel>("easy");
  const [sentence, setSentence] = useState<string | null>(null);
  const [phase, setPhase] = useState<RepeatPhase>("idle");
  const [heard, setHeard] = useState("");
  const [score, setScore] = useState<number | null>(null);
  const [attempts, setAttempts] = useState<{ score: number }[]>([]);
  const [speaking, setSpeaking] = useState(false);
  const visemeRef = useRef<string>("sil");
  const handleRef = useRef<SpeakHandle | null>(null);
  const [avatarOn, setAvatarOn] = useAvatarPref();

  useEffect(() => () => { handleRef.current?.cancel(); }, []);

  const play = useCallback((text: string) => {
    handleRef.current?.cancel();
    handleRef.current = speakExaminer(text, {
      onViseme: (v) => { visemeRef.current = v; },
      onStart: () => setSpeaking(true),
      onEnd: () => { setSpeaking(false); visemeRef.current = "sil"; },
    });
  }, []);

  function nextSentence() {
    const s = pickSentence(level, sentence ?? undefined);
    setSentence(s.text);
    setHeard("");
    setScore(null);
    setPhase("played");
    play(s.text);
  }

  async function scoreAttempt(text: string) {
    if (!sentence) return;
    const sc = repeatScore(sentence, text);
    setHeard(text);
    setScore(sc);
    setPhase("scored");
    setAttempts((a) => [...a, { score: sc }]);
    try { await store.recordRepeat(session.id, sentence, text, sc); } catch { /* local save failed — keep the UI result */ }
  }

  const avg = attempts.length ? Math.round(attempts.reduce((n, a) => n + a.score, 0) / attempts.length) : null;

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      padding: "max(1.5rem, env(safe-area-inset-top)) max(1.25rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.25rem, env(safe-area-inset-left))",
    }}>
      <div className="room-top" style={{ marginBottom: "0.6rem" }}>
        <button style={{ background: "none", border: 0, color: "var(--ink-2)", cursor: "pointer", font: "inherit", padding: "0.3rem 0", display: "inline-flex", alignItems: "center", gap: 5 }} onClick={onExit}>
          <X size={14} /> Done{avg != null ? ` · avg ${avg}%` : ""}
        </button>
        <span className="badge badge-make">free · on-device</span>
      </div>
      <h1 className="serif" style={{ fontSize: "1.65rem", margin: "0 0 0.8rem" }}>Hear it, say it back</h1>

      <div className="row" style={{ gap: "0.35rem", marginBottom: "0.85rem" }}>
        {(Object.keys(LEVEL_LABELS) as RepeatLevel[]).map((l) => (
          <button key={l} className={`chip toggle${level === l ? " selected" : ""}`} onClick={() => setLevel(l)}>{LEVEL_LABELS[l]}</button>
        ))}
        <label className="row muted" style={{ gap: "0.35rem", fontSize: "var(--fs-xxs)", cursor: "pointer", marginLeft: "auto" }}>
          <input type="checkbox" checked={avatarOn} onChange={(e) => setAvatarOn(e.target.checked)} style={{ width: "auto" }} />
          Face
        </label>
      </div>

      <div className="drill-stage" style={{ marginBottom: "0.85rem" }}>
        {avatarOn && (
          <div className="stage-face" style={{ width: 112, height: 112 }}>
            <Suspense fallback={<div className="skeleton-block" style={{ width: "100%", height: "100%", borderRadius: 999 }} />}>
              <ExaminerAvatar visemeRef={visemeRef} speaking={speaking} height={112} />
            </Suspense>
          </div>
        )}
        <SpeakLine live={speaking} flat={!speaking} />
        {phase === "idle" ? (
          <div style={{ textAlign: "center", padding: "0.75rem 0" }}>
            <p className="muted" style={{ fontSize: "var(--fs-sm)", lineHeight: 1.55, maxWidth: 380, margin: "0 auto 0.9rem" }}>
              You'll HEAR a sentence (it stays hidden). Repeat it out loud — the match is scored right here on your device. No AI call, nothing billed.
            </p>
            <button className="btn btn-primary btn-pill" onClick={nextSentence} disabled={!ttsAvailable()}>
              <Volume2 size={14} /> Play a sentence
            </button>
            {!ttsAvailable() && <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 8 }}>Text-to-speech isn't available in this browser.</div>}
          </div>
        ) : (
          <div>
            <div className="row" style={{ gap: "0.4rem", justifyContent: "center", margin: "0.5rem 0 0.75rem" }}>
              <button className="btn btn-sm btn-pill" onClick={() => sentence && play(sentence)} disabled={speaking}>
                <Volume2 size={13} /> Hear it again
              </button>
            </div>
            {phase === "played" && (
              <RepeatMic onText={(t) => void scoreAttempt(t)} />
            )}
            {phase === "scored" && sentence && score != null && (
              <div className="fade-up" style={{ textAlign: "center" }}>
                <div className={`drill-score serif tnum${score < 50 ? " low" : ""}`}>{score}%</div>
                <div className="drill-note muted">word match · scored on this phone</div>
                <p className="drill-words">
                  {targetWordMatches(sentence, heard).map((w, i) => (
                    <span key={i} className={w.hit ? undefined : "word-miss"}>{w.word} </span>
                  ))}
                </p>
                {heard && <div className="drill-heard muted">heard: “{heard}”</div>}
                <div className="row" style={{ gap: "0.5rem", justifyContent: "center", marginTop: "0.9rem" }}>
                  <button className="btn btn-pill" onClick={() => { setPhase("played"); setScore(null); setHeard(""); if (sentence) play(sentence); }}>
                    <RefreshCw size={13} /> Try again
                  </button>
                  <button className="btn btn-primary btn-pill" onClick={nextSentence}><Play size={13} /> Next sentence</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {attempts.length > 0 && (
        <div className="muted tnum" style={{ fontSize: "var(--fs-xs)", textAlign: "center" }}>
          {attempts.length} attempt{attempts.length !== 1 ? "s" : ""} this drill · average {avg}%
        </div>
      )}
    </main>
  );
}

// Single-shot repeat mic (the whole sentence is one utterance).
function RepeatMic({ onText }: { onText: (t: string) => void }) {
  const [listening, setListening] = useState(false);
  const recRef = useRef<any>(null);
  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* ignore */ } }, []);
  const SR = getSR();
  if (!SR) {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", textAlign: "center" }}>Dictation isn't available here — Listen & Repeat needs the device's speech recognition.</div>;
  }
  const toggle = () => {
    if (listening) { try { recRef.current?.stop(); } catch { /* ignore */ } setListening(false); return; }
    try {
      const rec = new SR();
      rec.lang = "en-US";
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.onresult = (e: any) => {
        const t = e?.results?.[0]?.[0]?.transcript;
        if (t) onText(String(t));
      };
      rec.onend = () => setListening(false);
      rec.onerror = () => setListening(false);
      recRef.current = rec;
      setListening(true);
      rec.start();
    } catch { setListening(false); }
  };
  return (
    <div style={{ textAlign: "center" }}>
      <button className={`btn btn-pill${listening ? " btn-primary" : ""}`} onClick={toggle} style={{ padding: "0.7rem 1.5rem" }}>
        {listening ? (<><Square size={14} /> Listening — tap when done</>) : (<><Mic size={15} /> Repeat it now</>)}
      </button>
    </div>
  );
}

// ── Session detail (history) ───────────────────────────────────────────────────

function SessionDetailModal({ session, onClose }: { session: Session; onClose: () => void }) {
  const [turns, setTurns] = useState<Turn[] | null>(null);
  const [feedbacks, setFeedbacks] = useState<{ part: Part; fb: SpeakingFeedback }[]>([]);
  useEffect(() => {
    void (async () => {
      setTurns(await store.listTurns(session.id));
      const rows = await store.listFeedbacks(session.id);
      setFeedbacks(rows.flatMap((r) => {
        const fb = parseFeedback(r.feedback_json);
        return fb ? [{ part: r.part, fb }] : [];
      }));
    })();
  }, [session.id]);

  const byPart = useMemo(() => {
    const m = new Map<Part, Turn[]>();
    for (const t of turns ?? []) {
      const arr = m.get(t.part) ?? [];
      arr.push(t);
      m.set(t.part, arr);
    }
    return m;
  }, [turns]);

  return (
    <ModalShell title={session.topic || MODE_LABELS[session.mode]} subtitle={`${EXAM_LABELS[session.exam]} · ${session.created_at.slice(0, 10)}`} onCancel={onClose} wide>
      {turns === null ? (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.5rem" }} aria-live="polite">
          {[0, 1, 2].map((i) => <div key={i} className="skeleton-block" style={{ height: 52 }} />)}
        </div>
      ) : turns.length === 0 ? (
        <div className="muted" style={{ fontSize: "var(--fs-sm)", textAlign: "center", padding: "1rem 0" }}>Nothing recorded in this session.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.75rem", maxHeight: 420, overflowY: "auto" }}>
          {[...byPart.entries()].map(([part, ts]) => (
            <div key={part}>
              <div className="stat-lbl" style={{ marginBottom: 4 }}>{PART_LABELS[part]}</div>
              {ts.map((t) => (
                <div key={t.id} style={{ padding: "0.6rem 0.7rem", background: "var(--elev-2)", borderRadius: 12, marginBottom: 6 }}>
                  <div style={{ fontWeight: 600, fontSize: "var(--fs-xs)", lineHeight: 1.5 }}>{t.prompt}</div>
                  {t.hint && <div className="muted" style={{ fontSize: "var(--fs-xxs)", whiteSpace: "pre-wrap", marginTop: 2 }}>{t.hint}</div>}
                  {t.transcript
                    ? <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 4, lineHeight: 1.55 }}>“{t.transcript}”{t.score != null && <strong style={{ color: "var(--accent)" }}> · {Math.round(t.score)}%</strong>}</div>
                    : <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: 4 }}>not answered</div>}
                </div>
              ))}
              {feedbacks.filter((f) => f.part === part).map((f, i) => (
                <FeedbackCard key={i} fb={f.fb} part={f.part} />
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "1.1rem" }}>
        <button className="btn btn-pill" onClick={onClose}>Close</button>
      </div>
    </ModalShell>
  );
}

// ── Shared bits (error card, copy button, modal shell) ─────────────────────────

function AskError({ err, onRetry }: { err: AskErr; onRetry: () => void }) {
  if (err.code === "insufficient_funds") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Coins size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-sm)", fontWeight: 600 }}>Add funds to keep practicing</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Sparkles size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "unauthenticated") {
    return (
      <div className="notice notice-info fade-up" style={{ marginTop: 12 }}>
        <Lock size={16} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted fade-up" style={{ fontSize: "var(--fs-sm)", marginTop: 12 }}>{err.message}</div>;
  }
  return (
    <div className="notice notice-err fade-up" style={{ marginTop: 12, justifyContent: "space-between", alignItems: "center" }}>
      <span style={{ fontSize: "var(--fs-sm)" }}>{err.message}</span>
      <button className="btn btn-sm btn-pill" style={{ flexShrink: 0 }} onClick={onRetry}>
        <RefreshCw size={12} /> Retry
      </button>
    </div>
  );
}

// Ghost copy-button for AI results (DESIGN-SYSTEM §7.5 — every Oriora result is copyable).
function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch { /* clipboard blocked — silently no-op */ }
  }, [text]);
  return (
    <button className="copy-btn" onClick={copy} aria-label={done ? "Copied" : label} title={done ? "Copied" : label}>
      <span aria-hidden>{done ? <Check size={13} /> : <Copy size={13} />}</span>
      <span className="copy-btn-lbl">{done ? "Copied" : label}</span>
      <span aria-live="polite" className="sr-only">{done ? "Copied to clipboard" : ""}</span>
    </button>
  );
}

const fieldLabel: React.CSSProperties = { display: "block", fontWeight: 600, fontSize: "var(--fs-sm)", marginBottom: "0.45rem" };

function ModalShell({ title, subtitle, children, onCancel, wide }: { title: string; subtitle?: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);
  return (
    <div role="dialog" aria-modal="true" aria-label={title} className="sheet-scrim" onClick={onCancel}>
      <div className="sheet sheet-center" onClick={(e) => e.stopPropagation()} style={{ width: wide ? "min(480px, calc(100% - 2rem))" : "min(400px, calc(100% - 2rem))", paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom))" }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.1rem", gap: "0.6rem" }}>
          <div style={{ minWidth: 0 }}>
            <div className="serif" style={{ fontWeight: 600, fontSize: "1.2rem", textTransform: "capitalize" }}>{title}</div>
            {subtitle && <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>}
          </div>
          <button className="icon-btn" aria-label="Close" onClick={onCancel} style={{ flexShrink: 0, marginTop: -2 }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// keep the unused invoke import shape consistent with the scaffold (web runtime swaps it)
void invoke;
void todayLocal;
